import React, { useCallback, useRef, useState } from 'react';
import { Upload, FileText, X, AlertCircle, CheckCircle2, Clock } from 'lucide-react';

interface RetainerDocumentUploadProps {
  file: File | null;
  onFileSelect: (file: File | null) => void;
  existingDocument?: {
    filename: string;
    status: 'pending' | 'mapped' | 'rejected';
  } | null;
  uploadStatus?: 'idle' | 'uploading' | 'success' | 'error';
  error?: string;
}

const ACCEPTED_EXTENSIONS = ['.pdf', '.doc', '.docx'];
const ACCEPTED_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB

export default function RetainerDocumentUpload({
  file,
  onFileSelect,
  existingDocument,
  uploadStatus = 'idle',
  error,
}: RetainerDocumentUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const validateFile = (f: File): string | null => {
    const ext = '.' + f.name.split('.').pop()?.toLowerCase();
    if (!ACCEPTED_EXTENSIONS.includes(ext)) {
      return 'Only .pdf, .doc, and .docx files are accepted.';
    }
    if (f.type && !ACCEPTED_MIME_TYPES.includes(f.type)) {
      return 'Invalid file type.';
    }
    if (f.size > MAX_FILE_SIZE) {
      return 'File size must be under 25 MB.';
    }
    return null;
  };

  const handleFile = useCallback(
    (f: File) => {
      const err = validateFile(f);
      if (err) {
        setValidationError(err);
        onFileSelect(null);
        return;
      }
      setValidationError(null);
      onFileSelect(f);
    },
    [onFileSelect],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragActive(false);
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleFile(e.dataTransfer.files[0]);
      }
    },
    [handleFile],
  );

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = () => setDragActive(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFile(e.target.files[0]);
    }
  };

  const handleRemove = () => {
    onFileSelect(null);
    setValidationError(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const displayError = validationError || error;
  const showExisting = !file && existingDocument;
  const showSelected = !!file && uploadStatus !== 'success';
  const showSuccess = uploadStatus === 'success';

  const statusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pending mapping by Fairday Ops';
      case 'mapped':
        return 'Mapped';
      case 'rejected':
        return 'Rejected';
      default:
        return status;
    }
  };

  const statusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4 text-amber-400" />;
      case 'mapped':
        return <CheckCircle2 className="w-4 h-4 text-green-400" />;
      case 'rejected':
        return <AlertCircle className="w-4 h-4 text-rose-400" />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-neutral-light">Retainer Document</label>
        <span className="text-xs text-accent">Required</span>
      </div>

      {/* Hidden file input (always in DOM) */}
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.doc,.docx"
        onChange={handleInputChange}
        className="hidden"
      />

      {/* Drag-and-drop zone */}
      {!showExisting && !showSuccess && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => inputRef.current?.click()}
          className={`flex flex-col items-center justify-center gap-3 p-6 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${
            dragActive
              ? 'border-accent bg-accent/10'
              : 'border-gray-700/50 bg-white/5 hover:border-gray-600'
          }`}
        >
          <Upload className="w-8 h-8 text-text-muted" />
          <div className="text-center">
            <p className="text-sm text-neutral-light">
              Drag & drop your PDF or DOCX here
            </p>
            <p className="text-xs text-text-muted mt-1">or</p>
            <p className="text-sm text-accent mt-1 hover:text-accent-hover">Choose file</p>
          </div>
        </div>
      )}

      {/* Selected file preview (pre-upload) */}
      {showSelected && file && (
        <div className="flex items-center justify-between p-3 bg-white/5 border border-gray-700/50 rounded-lg">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-accent" />
            <div>
              <p className="text-sm text-neutral-light">{file.name}</p>
              <p className="text-xs text-text-muted">
                {(file.size / 1024 / 1024).toFixed(2)} MB
              </p>
            </div>
          </div>
          {uploadStatus === 'uploading' ? (
            <span className="text-xs text-accent animate-pulse">Uploading...</span>
          ) : (
            <button type="button" onClick={handleRemove} className="p-1 hover:bg-white/10 rounded">
              <X className="w-4 h-4 text-text-muted" />
            </button>
          )}
        </div>
      )}

      {/* Upload success state */}
      {showSuccess && (
        <div className="flex items-center justify-between p-3 bg-white/5 border border-gray-700/50 rounded-lg">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-accent" />
            <div>
              <p className="text-sm text-neutral-light">{file?.name}</p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <Clock className="w-3.5 h-3.5 text-amber-400" />
                <span className="text-xs text-amber-400">Pending mapping by Fairday Ops</span>
              </div>
            </div>
          </div>
          <CheckCircle2 className="w-5 h-5 text-green-400" />
        </div>
      )}

      {/* Existing document (edit mode) */}
      {showExisting && existingDocument && (
        <div className="flex items-center justify-between p-3 bg-white/5 border border-gray-700/50 rounded-lg">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-accent" />
            <div>
              <p className="text-sm text-neutral-light">{existingDocument.filename}</p>
              <div className="flex items-center gap-1.5 mt-0.5">
                {statusIcon(existingDocument.status)}
                <span className="text-xs text-text-muted">
                  {statusLabel(existingDocument.status)}
                </span>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="text-xs text-accent hover:text-accent-hover"
          >
            Replace
          </button>
        </div>
      )}

      {/* Error display */}
      {displayError && (
        <div className="flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-rose-400" />
          <span className="text-xs text-rose-400">{displayError}</span>
        </div>
      )}
    </div>
  );
}
